/**
 * JD Volume pricing tiers
 * ---------------------------------------------------------------------------
 * Behaviour for the tier strip rendered by snippets/jd-volume-pricing.liquid.
 *
 * - Clicking a tier sets the product form's quantity input to that tier's floor.
 * - The tier whose range contains the current quantity is highlighted, however
 *   the quantity changed (stepper, typing, or theme JS).
 *
 * Implemented as a custom element rather than an inline script because Impact
 * re-renders product blocks on variant change (ProductRerender, theme.js), so
 * connected/disconnected callbacks are what keep the lifecycle honest.
 *
 * MOQ: nothing here enforces it. The quantity input already carries the minimum
 * order quantity as its `min` attribute (snippets/product-info.liquid), so the
 * clamp below is simply respecting a value the theme already set.
 */
if (!window.customElements.get('jd-volume-pricing')) {
  class JdVolumePricing extends HTMLElement {
    connectedCallback() {
      this._abortController = new AbortController();
      const signal = this._abortController.signal;

      this.addEventListener('click', this._onClick.bind(this), { signal });

      // Delegated so it survives the quantity input being replaced on variant change.
      const onQuantityEvent = this._onQuantityEvent.bind(this);
      document.addEventListener('change', onQuantityEvent, { capture: true, signal });
      document.addEventListener('input', onQuantityEvent, { capture: true, signal });

      this._observeForm();
      this._sync();
    }

    disconnectedCallback() {
      this._abortController?.abort();
    }

    /**
     * Impact re-renders the product form's blocks on variant change, but only for its own
     * built-in block types — this one is not on that list, so it has to pick its new markup out
     * of the fragment itself. Doing it this way keeps money formatting in Liquid.
     */
    _observeForm() {
      const form = document.forms[this.dataset.formId];

      if (!form) {
        // The form is rendered by the buy buttons block, which may come later in the DOM.
        requestAnimationFrame(() => {
          if (this.isConnected && !this._abortController.signal.aborted) this._observeForm();
        });
        return;
      }

      form.addEventListener('product:rerender', this._onRerender.bind(this), {
        signal: this._abortController.signal
      });
    }

    get tiers() {
      return Array.from(this.querySelectorAll('.jd-vp__tier'));
    }

    /**
     * The quantity input is bound to the form by a `form` attribute rather than by nesting, so
     * the form's element collection is the exact lookup. Resolved on every use — never cached,
     * because the quantity selector block is replaced wholesale on variant change.
     */
    _quantityInput() {
      const formId = this.dataset.formId;
      const input = document.forms[formId]?.elements?.quantity;

      if (input instanceof HTMLInputElement) return input;

      return document.querySelector(`input[name="quantity"][form="${formId}"]`);
    }

    _onQuantityEvent(event) {
      const target = event.target;

      if (
        target instanceof HTMLInputElement &&
        target.name === 'quantity' &&
        target.getAttribute('form') === this.dataset.formId
      ) {
        this._sync();
      }
    }

    _onClick(event) {
      const tier = event.target.closest('.jd-vp__tier');

      if (!tier || !this.contains(tier)) return;

      event.preventDefault();

      // Clicking the tier you are already in would drop the quantity back to its floor.
      if (tier.classList.contains('is-active')) return;

      this._setQuantity(parseInt(tier.dataset.min, 10) || 1);
    }

    _onRerender(event) {
      // A full re-render replaces this element outright; the replacement syncs itself.
      if (!this.isConnected) return;

      const fragment = event.detail?.htmlFragment;
      const next = fragment?.querySelector(`jd-volume-pricing[data-block-id="${this.dataset.blockId}"]`);

      if (!next) return;

      this.dataset.moq = next.dataset.moq;
      this.dataset.step = next.dataset.step;
      this.setAttribute('style', next.getAttribute('style') || '');
      this.innerHTML = next.innerHTML;

      // The quantity selector block is restored programmatically, without an event, so re-read
      // it once the rest of the re-render has settled.
      requestAnimationFrame(() => {
        if (this.isConnected) this._sync();
      });
    }

    _sync() {
      const input = this._quantityInput();

      if (!input) return;

      const quantity = parseInt(input.value, 10);

      this.tiers.forEach((tier) => {
        const min = parseInt(tier.dataset.min, 10) || 1;
        const max = tier.dataset.max ? parseInt(tier.dataset.max, 10) : Infinity;
        // An empty or invalid input highlights nothing.
        const active = !isNaN(quantity) && quantity >= min && quantity <= max;

        tier.classList.toggle('is-active', active);
        tier.setAttribute('aria-pressed', active ? 'true' : 'false');
      });
    }

    _setQuantity(value) {
      const input = this._quantityInput();

      if (!input) return;

      let min = parseInt(input.min, 10);
      if (isNaN(min)) min = parseInt(this.dataset.moq, 10) || 1;

      let step = parseInt(input.step, 10);
      if (isNaN(step) || step < 1) step = parseInt(this.dataset.step, 10) || 1;

      const max = parseInt(input.max, 10);

      if (value < min) value = min;

      // Steps are counted from the floor, which is not necessarily a multiple of the step itself.
      if (step > 1) value = min + Math.ceil((value - min) / step) * step;

      if (!isNaN(max) && max > 0 && value > max) value = max;

      input.value = String(value);

      // Same event the theme's own +/- buttons fire (QuantitySelector in theme.js).
      input.dispatchEvent(new Event('change', { bubbles: true }));

      this._sync();
    }
  }

  window.customElements.define('jd-volume-pricing', JdVolumePricing);
}
